import styled from "styled-components";
import { useEffect, useMemo, useState } from "react";
import { X, Check, AlertTriangle, Clock } from "lucide-react";
import { router } from "@inertiajs/react";

import { theme } from "./theme";
import { useT } from "./i18n";
import DatePickField from "./DatePickField";
import { wibTodayIso } from "./dateFormat";
import { apiFetch } from "./api";

/* Block or unblock MANY rooms across a date range in one action.
 *
 * Two steps in one panel, the MoveBlockModal shape: pick, then confirm against a server-computed
 * preview. The preview is not decoration — a range almost always contains nights that cannot be
 * blocked (already reserved) and the operator has to see which before committing. It is advisory
 * only: the executor re-plans under the lock, because a booking can land between the two clicks.
 */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(26, 26, 26, 0.5);
  backdrop-filter: blur(2px);
`;

const Panel = styled.div`
  width: 100%;
  max-width: 720px;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border-radius: 14px;
  border-top: 3px solid ${theme.gold};
  box-shadow: 0 24px 60px -18px rgba(26, 26, 26, 0.5);
  overflow: hidden;
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.9rem 1rem 0.6rem;
  border-bottom: 1px solid ${theme.border};
`;

const Title = styled.div`
  font-size: 1rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.9rem;
  height: 1.9rem;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  background: #ffffff;
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Body = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0.85rem 1.1rem;
`;

const Muted = styled.div`
  padding: 0.6rem 0.25rem;
  font-size: 0.9rem;
  color: ${theme.muted};
`;

const ErrorNote = styled.div`
  margin-bottom: 0.7rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: #fdecec;
  border: 1px solid #f2c4c0;
  color: #b91c1c;
  font-size: 0.85rem;
  font-weight: 600;
`;

const Section = styled.div`
  margin-bottom: 0.9rem;
`;

const Label = styled.div`
  margin-bottom: 0.35rem;
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${theme.ink700};
`;

const ModeRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ModeButton = styled.button`
  flex: 1 1 0;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${(props) => (props.$active ? theme.ink : theme.border)};
  border-radius: 9px;
  background: ${(props) => (props.$active ? theme.ink : "#ffffff")};
  color: ${(props) => (props.$active ? theme.gold : theme.ink700)};
  font-size: 0.86rem;
  font-weight: 800;
  cursor: pointer;
`;

const Dates = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const Select = styled.select`
  padding: 0.45rem 0.6rem;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  font-size: 0.88rem;
  font-weight: 600;
  color: ${theme.ink};
  background: #ffffff;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.45rem 0.6rem;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  font-size: 0.88rem;
  color: ${theme.ink};
`;

const TypeBlock = styled.div`
  margin-bottom: 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  overflow: hidden;
`;

const TypeHead = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.7rem;
  background: ${theme.goldSoft};
  border: 0;
  border-bottom: 1px solid ${theme.border};
  font-size: 0.85rem;
  font-weight: 800;
  color: ${theme.ink};
  cursor: pointer;
  text-align: left;
`;

const TypeCount = styled.span`
  margin-left: auto;
  font-size: 0.78rem;
  font-weight: 700;
  color: ${theme.ink700};
`;

const RoomGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  padding: 0.55rem 0.7rem;
`;

const RoomChip = styled.button`
  padding: 0.3rem 0.6rem;
  border: 1px solid ${(props) => (props.$on ? "#2e7d32" : theme.border)};
  border-radius: 999px;
  background: ${(props) => (props.$on ? "#e8f5e9" : "#ffffff")};
  color: ${(props) => (props.$on ? "#1b5e20" : theme.ink700)};
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
`;

const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.8rem;
`;

const Stat = styled.div`
  flex: 1 1 8rem;
  padding: 0.55rem 0.7rem;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  background: ${(props) => (props.$strong ? theme.goldSoft : "#ffffff")};
`;

const StatValue = styled.div`
  font-size: 1.15rem;
  font-weight: 800;
  color: ${theme.ink};
`;

const StatLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${theme.ink700};
`;

const SkipList = styled.div`
  margin-top: 0.5rem;
  border: 1px solid ${theme.border};
  border-radius: 10px;
  overflow: hidden;
`;

const SkipRow = styled.div`
  display: flex;
  gap: 0.5rem;
  padding: 0.4rem 0.7rem;
  font-size: 0.82rem;
  color: ${theme.ink700};
  border-bottom: 1px solid ${theme.border};
  &:last-child {
    border-bottom: 0;
  }
`;

const SkipRoom = styled.span`
  min-width: 5.5rem;
  font-weight: 800;
  color: ${theme.ink};
`;

/* Amber, not red: a day-use guest in the room is a consequence the operator should see, never a
 * refusal. A transit stay sells hours, so the night itself is unsold and blockable.
 */
const WarnNote = styled.div`
  margin-top: 0.7rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: #fff8e6;
  border: 1px solid #f2d492;
  color: #8a4b00;
  font-size: 0.83rem;
  line-height: 1.45;
`;

const PushNote = styled.div`
  margin-top: 0.7rem;
  padding: 0.5rem 0.7rem;
  border-radius: 8px;
  background: #f4f6f8;
  border: 1px solid ${theme.border};
  color: ${theme.ink700};
  font-size: 0.82rem;
  line-height: 1.45;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 0.7rem 1.1rem;
  border-top: 1px solid ${theme.border};
`;

const Selected = styled.span`
  margin-right: auto;
  font-size: 0.84rem;
  color: ${theme.muted};
`;

const Cancel = styled.button`
  padding: 0.45rem 1rem;
  border: 1px solid ${theme.border};
  border-radius: 9px;
  background: #ffffff;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${theme.ink700};
  cursor: pointer;
  &:hover {
    background: ${theme.goldSoft};
  }
`;

const Confirm = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.45rem 1.1rem;
  border: 0;
  border-radius: 9px;
  background: ${theme.ink};
  color: ${theme.gold};
  font-size: 0.9rem;
  font-weight: 800;
  cursor: pointer;
  &:hover:not(:disabled) {
    background: ${theme.inkSoft};
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const SKIP_LABEL_KEYS = {
  skipped_held: "bulk_block_skip_held",
  skipped_close_out: "bulk_block_skip_close_out",
  already_in_state: "bulk_block_skip_already",
};

const BulkBlockModal = (props) => {
  const { t } = useT();
  const startDate = props.date || wibTodayIso();

  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [mode, setMode] = useState("block");
  const [from, setFrom] = useState(startDate);
  const [to, setTo] = useState(startDate);
  const [selected, setSelected] = useState([]);
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;

    apiFetch("/frontdesk_v2/rooms/bulk_block_options")
      .then((res) => {
        if (!active) return;
        if (res && res.data) {
          setOptions(res.data);
        } else {
          setError(res?.message || t("error_body"));
        }
      })
      .catch(() => active && setError(t("error_body")))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [t]);

  const roomTypes = options?.room_types || [];
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggleRoom = (roomId) => {
    setPreview(null);
    setSelected((current) =>
      current.includes(roomId)
        ? current.filter((id) => id !== roomId)
        : current.concat(roomId),
    );
  };

  const toggleType = (group) => {
    setPreview(null);
    const ids = group.rooms.map((room) => room.id);
    const allOn = ids.every((id) => selectedSet.has(id));
    setSelected((current) =>
      allOn
        ? current.filter((id) => !ids.includes(id))
        : current.concat(ids.filter((id) => !current.includes(id))),
    );
  };

  const params = () => ({
    room_ids: selected,
    start_date: from,
    end_date: to,
    action_kind: mode,
    block_reason: mode === "block" ? reason : "",
    block_note: note,
  });

  const runPreview = () => {
    setBusy(true);
    setError("");

    apiFetch("/frontdesk_v2/rooms/bulk_block_preview", {
      method: "POST",
      body: JSON.stringify(params()),
    })
      .then((res) => {
        if (res && res.data) {
          setPreview(res.data);
        } else {
          setError(res?.message || t("error_body"));
        }
      })
      .catch(() => setError(t("error_body")))
      .finally(() => setBusy(false));
  };

  /* Redirect + flash, the same path every other rack write takes — the toast and the grid reload
   * both come from the controller, so there is no second source of truth for the result.
   */
  const submit = () => {
    setBusy(true);
    router.post(
      "/frontdesk_v2/rooms/bulk_block",
      {
        ...params(),
        return_to: window.location.pathname + window.location.search,
      },
      { preserveScroll: true },
    );
    props.onClose();
  };

  const canPreview =
    selected.length > 0 &&
    Boolean(from) &&
    Boolean(to) &&
    (mode === "unblock" || Boolean(reason));

  const skipped = (preview?.decisions || []).filter((decision) =>
    Object.keys(SKIP_LABEL_KEYS).includes(decision.verdict),
  );
  const transitRooms = [
    ...new Set(
      (preview?.decisions || [])
        .filter((decision) => decision.transit && decision.verdict === "apply")
        .map((decision) => decision.room_code),
    ),
  ];

  const renderForm = () => (
    <>
      <Section>
        <Label>{t("bulk_block_mode")}</Label>
        <ModeRow>
          <ModeButton
            type="button"
            $active={mode === "block"}
            onClick={() => {
              setMode("block");
              setPreview(null);
            }}
          >
            {t("block_room")}
          </ModeButton>
          <ModeButton
            type="button"
            $active={mode === "unblock"}
            onClick={() => {
              setMode("unblock");
              setPreview(null);
            }}
          >
            {t("unblock_room")}
          </ModeButton>
        </ModeRow>
      </Section>

      <Section>
        <Dates>
          <DatePickField
            label={t("bulk_block_from")}
            value={from}
            onChange={(value) => {
              setFrom(value);
              setPreview(null);
              if (value && to && value > to) setTo(value);
            }}
          />
          <DatePickField
            label={t("bulk_block_to")}
            value={to}
            min={from}
            onChange={(value) => {
              setTo(value);
              setPreview(null);
            }}
          />
          <Muted>
            {t("bulk_block_max_range")} {options?.max_range_days}
          </Muted>
        </Dates>
      </Section>

      {mode === "block" ? (
        <Section>
          <Label>{t("block_reason_label")}</Label>
          <Select
            value={reason}
            onChange={(event) => {
              setReason(event.target.value);
              setPreview(null);
            }}
          >
            <option value="">—</option>
            {(options?.block_reasons || []).map((value) => (
              <option key={value} value={value}>
                {t(`block_reason_${value}`)}
              </option>
            ))}
          </Select>
          <div style={{ marginTop: "0.5rem" }}>
            <Label>{t("block_note_label")}</Label>
            <TextInput
              value={note}
              placeholder={t("block_note_placeholder")}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
        </Section>
      ) : null}

      <Section>
        <Label>{t("bulk_block_rooms")}</Label>
        {roomTypes.map((group) => {
          const ids = group.rooms.map((room) => room.id);
          const on = ids.filter((id) => selectedSet.has(id)).length;
          return (
            <TypeBlock key={group.id || group.name}>
              <TypeHead type="button" onClick={() => toggleType(group)}>
                {group.name}
                <TypeCount>
                  {on}/{ids.length}
                </TypeCount>
              </TypeHead>
              <RoomGrid>
                {group.rooms.map((room) => (
                  <RoomChip
                    key={room.id}
                    type="button"
                    $on={selectedSet.has(room.id)}
                    onClick={() => toggleRoom(room.id)}
                  >
                    {room.code}
                  </RoomChip>
                ))}
              </RoomGrid>
            </TypeBlock>
          );
        })}
      </Section>
    </>
  );

  const renderPreview = () => (
    <>
      <Summary>
        <Stat $strong>
          <StatValue>{preview.counts.room_nights_applied}</StatValue>
          <StatLabel>{t("bulk_block_will_apply")}</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{preview.counts.room_nights_skipped_held}</StatValue>
          <StatLabel>{t("bulk_block_skip_held")}</StatLabel>
        </Stat>
        <Stat>
          <StatValue>{preview.counts.room_nights_already_in_state}</StatValue>
          <StatLabel>{t("bulk_block_skip_already")}</StatLabel>
        </Stat>
      </Summary>

      {skipped.length > 0 ? (
        <SkipList>
          {skipped.slice(0, 40).map((decision) => (
            <SkipRow key={`${decision.room_id}-${decision.date}`}>
              <SkipRoom>{decision.room_code}</SkipRoom>
              <span>{decision.date}</span>
              <span>
                {decision.message || t(SKIP_LABEL_KEYS[decision.verdict])}
              </span>
            </SkipRow>
          ))}
        </SkipList>
      ) : null}

      {transitRooms.length > 0 ? (
        <WarnNote>
          <Clock size={13} /> {t("bulk_block_transit_note")}{" "}
          {transitRooms.join(", ")}
        </WarnNote>
      ) : null}

      {preview.counts.room_nights_local_only_past > 0 ? (
        <WarnNote>
          <AlertTriangle size={13} /> {t("bulk_block_past_note")}
        </WarnNote>
      ) : null}

      <PushNote>
        {t("bulk_block_push_note")}
        {(preview.room_types || []).map((group) => (
          <div key={group.ezee_room_type_id || group.name}>
            • {group.name} — {group.pushable_dates} {t("bulk_block_nights")}
          </div>
        ))}
      </PushNote>
    </>
  );

  return (
    <Overlay onClick={props.onClose}>
      <Panel onClick={(event) => event.stopPropagation()}>
        <Head>
          <Title>{t("bulk_block_title")}</Title>
          <IconButton type="button" onClick={props.onClose}>
            <X size={15} />
          </IconButton>
        </Head>

        <Body>
          {error ? <ErrorNote>{error}</ErrorNote> : null}
          {loading ? (
            <Muted>{t("loading")}</Muted>
          ) : preview ? (
            renderPreview()
          ) : (
            renderForm()
          )}
        </Body>

        <Foot>
          <Selected>
            {selected.length} {t("bulk_block_rooms_selected")}
          </Selected>
          <Cancel
            type="button"
            onClick={() => (preview ? setPreview(null) : props.onClose())}
          >
            {preview ? t("bulk_block_back") : t("bulk_block_cancel")}
          </Cancel>
          <Confirm
            type="button"
            disabled={busy || !canPreview}
            onClick={() => (preview ? submit() : runPreview())}
          >
            <Check size={15} />
            {preview ? t("bulk_block_ok") : t("bulk_block_check")}
          </Confirm>
        </Foot>
      </Panel>
    </Overlay>
  );
};

export default BulkBlockModal;
